import { chunkPairs } from "@memorang/helpers/array";
import { Divider } from "react-native-paper";
import { useAppTheme } from "../../../useAppTheme";
import { useDevice } from "../../../useDevice";
import Box from "../../Box";
import { RecommendedCardSkeleton } from "../../HomeApplet/RecommendedCardSkeleton";
import { Skeleton } from "../../Skeleton";

export type SessionReportSummarySkeletonProps = {
	donutCount?: number;
	statCount?: number;
	showInsight?: boolean;
};

const DONUT_SIZE = 100;
const HALO_PADDING = 10;
const DONUT_SKELETON_SLOTS = [
	"donut-a",
	"donut-b",
	"donut-c",
	"donut-d",
] as const;
const STAT_SKELETON_SLOTS = ["stat-a", "stat-b", "stat-c", "stat-d"] as const;

const StatSkeletonCell = ({ borderRadius }: { borderRadius: number }) => (
	<Box flex={1} minWidth={0} style={{ flexBasis: 0 }}>
		<Box
			gap={4}
			paddingHorizontal={12}
			paddingVertical={8}
			borderRadius={borderRadius}
			width="100%"
			alignItems="center"
			justifyContent="center"
		>
			<Skeleton variant="circle" size={24} />
			<Skeleton width="80%" height={16} />
			<Skeleton width="100%" height={16} />
		</Box>
	</Box>
);

export const SessionReportSummarySkeleton = ({
	donutCount = 1,
	statCount = 3,
	showInsight = true,
}: SessionReportSummarySkeletonProps) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const donutSlots = DONUT_SKELETON_SLOTS.slice(0, donutCount);
	const statSlots = STAT_SKELETON_SLOTS.slice(0, statCount);
	const isSingleDonut = donutCount === 1;
	const statTileRadius = theme.roundness * 4;

	return (
		<Box
			width="100%"
			backgroundColor={theme.colors.surface}
			borderRadius={isMobile ? 0 : theme.roundness * 3}
			marginTop={isSingleDonut ? 48 : 0}
			overflow="visible"
		>
			<Box
				gap={24}
				paddingHorizontal={16}
				paddingTop={24}
				paddingBottom={16}
				overflow="visible"
			>
				<Box gap={16} width="100%">
					<Box
						flexDirection="row"
						gap={isMobile ? 24 : 62}
						justifyContent="center"
						marginTop={isSingleDonut ? -72 : 0}
					>
						{donutSlots.map((slotId) => (
							<Box key={slotId} gap={8} alignItems="center">
								<Box
									padding={HALO_PADDING}
									borderRadius={(DONUT_SIZE + HALO_PADDING * 2) / 2}
									backgroundColor={theme.colors.surface}
								>
									<Skeleton variant="circle" size={DONUT_SIZE} />
								</Box>
								{isSingleDonut ? null : <Skeleton height={14} width={80} />}
							</Box>
						))}
					</Box>
					<Box gap={6} width="100%" alignItems="center">
						<Skeleton height={14} width="90%" />
						<Skeleton height={14} width="55%" />
					</Box>
				</Box>
				{isMobile ? (
					<Box gap={2} width="100%">
						{chunkPairs(statSlots).map((pair) => (
							<Box
								key={pair.join("-")}
								flexDirection="row"
								gap={2}
								width="100%"
							>
								{pair.map((slotId) => (
									<StatSkeletonCell
										key={slotId}
										borderRadius={statTileRadius}
									/>
								))}
							</Box>
						))}
					</Box>
				) : (
					<Box flexDirection="row" gap={2} width="100%">
						{statSlots.map((slotId) => (
							<StatSkeletonCell key={slotId} borderRadius={statTileRadius} />
						))}
					</Box>
				)}
			</Box>
			{showInsight ? (
				<>
					<Divider style={{ backgroundColor: theme.colors.surfaceVariant }} />
					<RecommendedCardSkeleton />
					<Divider style={{ backgroundColor: theme.colors.surfaceVariant }} />
					<Box padding={16} width="100%">
						<Skeleton
							height={64}
							width="100%"
							borderRadius={theme.roundness * 3}
						/>
					</Box>
				</>
			) : null}
		</Box>
	);
};
